<script lang="ts" setup>
import { useTemplateRef } from "vue";
import type { MenuItemProps, MenuItemSlots } from "./";
import { useMenuItem } from "./useMenuItem";
import { useSlotPresence } from "composables/useSlotPresence";

defineOptions({ name: "MenuItem" });

const props = withDefaults(defineProps<MenuItemProps>(), {
  selected: false,
  open: false,
  type: "link",
  children: () => [],
});

defineSlots<MenuItemSlots>();

const triggerRef = useTemplateRef<HTMLElement>("triggerRef");

const {
  state,
  classes,
  openedProxy,
  isCollapsed,
  isChild,
  isExpandable,
  hasChildren,
  isActive,
  isExternalTarget,
  externalRel,
  detailsTrailingIconName,
} = useMenuItem({ props, triggerRef });

const { default: hasDefault } = useSlotPresence(["default"]); // 'default' is the default slot name
</script>

<template>
  <li ref="triggerRef" part="item" :class="[type === 'label' && classes.label]">
    <a
      v-if="!isExpandable"
      :class="[type !== 'label' && (isChild ? classes.submenuItem : classes.base)]"
      class="e-menu-item"
      :href="to"
      :target="target ?? undefined"
      :rel="externalRel"
      :aria-disabled="disabled"
      :aria-current="isActive && to ? 'page' : undefined"
    >
      <div part="link-icon" :class="classes.icon" class="e-menu-icon">
        <slot name="leading">
          <e-icon
            v-if="icon && type !== 'label'"
            exportparts="base:link-icon-base"
            :size="isChild ? 'xs' : 'md'"
            :name="icon"
          ></e-icon>
        </slot>
      </div>

      <!-- default slot renders in the active branch only (link or details); both branches share the same slot content -->
      <div v-show="!isCollapsed" class="contents">
        <slot />
        <span v-if="!hasDefault" :class="classes.title" class="e-menu-item-title">
          {{ state.label?.value }}
        </span>
      </div>

      <slot name="trailing">
        <div v-if="isExternalTarget && !isCollapsed" :class="classes.icon" class="e-menu-icon">
          <e-icon
            :class="classes.detailsTrailingIcon"
            class="e-menu-item-details-trailing-icon"
            name="open-in-new"
          ></e-icon>
        </div>
      </slot>
    </a>

    <details
      v-else
      part="details"
      :class="classes.details"
      class="e-menu-item-details"
      :open="openedProxy && !isCollapsed"
      @toggle="openedProxy = ($event.target as HTMLDetailsElement).open"
    >
      <summary part="summary" :class="classes.detailsSummary" class="e-menu-item-details-summary">
        <span :class="[isChild ? classes.submenuItem : classes.base]" class="e-menu-item">
          <div part="summary-icon" :class="classes.icon" class="e-menu-icon">
            <slot name="leading">
              <e-icon
                v-if="icon && type !== 'label'"
                exportparts="base:summary-icon-base"
                :size="isChild ? 'xs' : 'md'"
                :name="icon"
              ></e-icon>
            </slot>
          </div>

          <div v-show="!isCollapsed" class="contents">
            <slot />
            <span v-if="!hasDefault" :class="classes.title" class="e-menu-item-title">
              {{ state.label?.value }}
            </span>
          </div>

          <e-icon
            v-if="detailsTrailingIconName"
            :class="classes.detailsTrailingIcon"
            class="e-menu-icon e-menu-item-details-trailing-icon"
            :name="detailsTrailingIconName"
          ></e-icon>
        </span>
      </summary>
      <ul v-show="hasChildren && !isCollapsed" :class="classes.detailsContent" class="e-menu-item-details-content">
        <slot name="children">
          <e-menu-item v-for="(child, index) in children" :key="index" v-bind="child" />
        </slot>
      </ul>
    </details>
  </li>
</template>

<style>
:host {
  display: contents;
}
</style>
